<template>
  <div class="homePage">
    <Loader v-if="loading" />

    <div v-else>
      <div v-if="noData">
        <p class="text-center">{{ $t("labels.welcomeMsg") }}</p>
      </div>
      <NoData v-if="noData" />

      <div class="" ref="main_wrapper" v-else>
        <div class="row fadeIn">
          <div class="col-12 py-0">
            <main>
              <!-- Statistics Cards (keeping your existing implementation) -->
              <div class="row">
                <div
                  class="col-md-4"
                  v-for="card in statistics.statistics"
                  :key="card.id"
                >
                  <simple-card classes="w-100">
                    <statistics-card
                      :item="card"
                      :color="getRandomColor(colors)"
                    />
                  </simple-card>
                </div>
              </div>

              <!-- Charts Row -->
              <div class="row">
                <!-- Orders Count Chart -->
                <div class="col-lg-6">
                  <div class="chart-card gradient-border">
                    <div class="chart-header">
                      <div class="chart-icon orders-icon">
                        <i class="fas fa-chart-line"></i>
                      </div>
                      <div class="chart-title-section">
                        <h3 class="chart-title">{{ $t("labels.ordersCount") }}</h3>
                        <p class="chart-subtitle">
                          {{ $t("labels.ordersCountDescription") }}
                        </p>
                      </div>
                    </div>
                    <div class="chart-container">
                      <apexchart
                        type="area"
                        height="350"
                        :options="ordersChartOptions"
                        :series="ordersChartSeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>

                <!-- Revenue Chart -->
                <div class="col-lg-6">
                  <div class="chart-card gradient-border">
                    <div class="chart-header">
                      <div class="chart-icon revenue-icon">
                        <i class="fas fa-dollar-sign"></i>
                      </div>
                      <div class="chart-title-section">
                        <h3 class="chart-title">{{ $t("labels.revenueCount") }}</h3>
                        <p class="chart-subtitle">{{ $t("labels.revenueCountDescription") }}</p>
                      </div>
                    </div>
                    <div class="chart-container">
                      <apexchart
                        type="bar"
                        height="350"
                        :options="revenueChartOptions"
                        :series="revenueChartSeries"
                      ></apexchart>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Analytics Row -->
              <div class="row">
                <!-- Gender Distribution -->
                <div class="col-md-6">
                  <div class="analytics-card gradient-border">
                    <div class="analytics-header">
                      <div class="analytics-icon gender-icon">
                        <i class="fas fa-users"></i>
                      </div>
                      <h3 class="analytics-title">{{ $t("labels.genderDistribution") }}</h3>
                    </div>
                    <div class="chart-container gender">
                      <apexchart
                        type="donut"
                        height="200"
                        :options="genderChartOptions"
                        :series="genderChartSeries"
                      ></apexchart>
                    </div>
                    <div class="gender-legend">
                      <div
                        class="legend-item"
                        v-for="(gender, index) in statistics
                          .user_count_geneder_graph.gender"
                        :key="index"
                      >
                        <div
                          class="legend-color"
                          :style="{ backgroundColor: genderColors[index] }"
                        ></div>
                        <span class="legend-label">{{
                          gender === "female" ? $t("labels.female") : $t("labels.male")
                        }}</span>
                        <span class="legend-count">{{
                          statistics.user_count_geneder_graph.users_count[index]
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Top Client Card -->
                <div class="col-md-6">
                  <div class="client-card gradient-border h-100">
                    <div class="client-header">
                      <div class="client-badge">
                        <i class="fas fa-crown"></i>
                      </div>
                      <h3 class="analytics-title">{{ $t("labels.topClient") }}</h3>
                    </div>
                    <div class="client-content">
                      <div class="client-avatar-wrapper">
                        <div class="client-avatar">
                          <img
                            :src="statistics.max_client_orderd.image"
                            :alt="statistics.max_client_orderd.name"
                          />
                          <!-- <div class="client-status"></div> -->
                        </div>
                        <!-- <div class="client-glow"></div> -->
                      </div>
                      <div class="client-info">
                        <h4 class="client-name">
                          {{ statistics.max_client_orderd.name }}
                        </h4>
                        <p class="client-email">
                          {{ statistics.max_client_orderd.email }}
                        </p>
                        <div
                          class="client-stats gap-2 d-flex align-items-center justify-content-center"
                        >
                          <div
                            class="stat-item client-stats gap-2 d-flex align-items-center justify-content-center"
                          >
                            <i class="fas fa-shopping-bag"></i>
                            <span>{{ $t("labels.mostOrders") }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Service Ratings Table -->
              <div class="row">
                <!-- Service Groups Performance -->
                <div class="col-12">
                  <div class="service-card gradient-border">
                    <div class="service-header">
                      <div class="service-icon">
                        <i class="fas fa-cut"></i>
                      </div>
                      <h3 class="service-title">{{ $t("labels.topServices") }}</h3>
                    </div>
                    <div class="service-list">
                      <div
                        class="service-item"
                        v-for="service in statistics.provider_services_count_per_group_service_table"
                        :key="service.group_service_id"
                      >
                        <div class="service-item-icon">
                          <i class="fas fa-spa"></i>
                        </div>
                        <div class="service-info">
                          <div class="service-name">
                            {{ service.group_name }}
                          </div>
                          <div class="service-orders">
                            {{ service.orders_count }} {{ $t("labels.order") }}
                          </div>
                        </div>
                        
                      </div>
                      
                    </div>
                  </div>
                </div>
                
              </div>

              <!-- Provider Tables (keeping your existing implementation) -->
              <div class="row mb-6">
                <div class="col-12">
                  <simple-card classes="w-100 m-0">
                    <main>
                      <v-data-table
                        class="thumb strip"
                        :headers="top_providers_headers"
                        :items="statistics.top_providers"
                        :loading="loading"
                        :loading-text="$t('table.loadingData')"
                        item-key="id"
                        hide-default-footer
                        disable-pagination
                      >
                        <!-- Your existing table templates -->
                        <template v-slot:[`item.index`]="{ index }">
                          {{ index + 1 }}
                        </template>
                        <template v-slot:[`item.avatar`]="{ item }">
                          <img
                            @click="show_model_1(item.avatar)"
                            class="image"
                            :src="item.avatar"
                            v-if="item.avatar"
                          />
                          <span class="redColor fontBold" v-else>{{
                            $t("notFound")
                          }}</span>
                        </template>
                        <template v-slot:[`item.name`]="{ item }">
                          <router-link
                            v-if="item.full_name"
                            :to="`/riders/show/${item?.id}`"
                          >
                            <span>{{ item.full_name }}</span>
                          </router-link>
                          <span class="redColor fontBold" v-else>{{
                            $t("notFound")
                          }}</span>
                        </template>
                        <template v-slot:[`item.email`]="{ item }">
                          <span v-if="item.email">{{ item.email }}</span>
                          <span class="redColor fontBold" v-else>{{
                            $t("notFound")
                          }}</span>
                        </template>
                        <template v-slot:no-data>{{
                          $t("table.noData")
                        }}</template>
                        <template v-slot:[`item.is_active`]="{ item }">
                          <span
                            class="status"
                            :class="item.is_active ? 'active' : 'inactive'"
                          >
                            {{ helper_boolToText(item.is_active) }}
                          </span>
                        </template>
                        <template
                          v-slot:[`item.phone_complete_form`]="{ item }"
                        >
                          <a
                            :href="`tel:${item.phone_complete_form}`"
                            v-if="item.phone_complete_form"
                          >
                            <bdi>{{ item.phone_complete_form }}</bdi>
                          </a>
                          <span class="redColor fontBold" v-else>{{
                            $t("notFound")
                          }}</span>
                        </template>
                        <template v-slot:[`item.actions`]="{ item }">
                          <div class="_actions">
                            <router-link
                              :to="`/providers/show/${item.id}/cards`"
                            >
                              <v-icon class="show" small> fal fa-eye </v-icon>
                            </router-link>
                          </div>
                        </template>
                        <template v-slot:top>
                          <h3 class="table-title title">
                            {{ $t("labels.topRateProviders") }}
                          </h3>
                        </template>
                      </v-data-table>
                    </main>
                  </simple-card>
                </div>

             

                <div class="col-12">
                  <div class="ratings-card gradient-border">
                    <div class="ratings-header">
                      <div class="ratings-icon">
                        <i class="fas fa-star"></i>
                      </div>
                      <h3 class="ratings-title">{{ $t("labels.serviceRatings") }}</h3>
                    </div>
                    <div class="ratings-grid">
                      <div
                        class="rating-item"
                        v-for="(
                          service, index
                        ) in statistics.provider_services_rate_avg_table"
                        :key="index"
                      >
                      <span class="service-name-small">{{
                            service.provider_name
                          }}</span>
                        <div class="rating-service">
                        
                          
                          <span style="font-size: 16px;" class="service-name-small">{{
                            service.service_name
                          }}</span>
                        </div>
                        <div class="rating-stars">
                          <div class="stars-container">
                            <div class="stars-bg">
                              <i
                                class="fas fa-star"
                                v-for="n in 5"
                                :key="`bg-${n}`"
                              ></i>
                            </div>
                            <div
                              class="stars-fill"
                              :style="{
                                width:
                                  ((service.rate_avg || 0) / 5) * 100 + '%',
                              }"
                            >
                              <i
                                class="fas fa-star"
                                v-for="n in 5"
                                :key="`fill-${n}`"
                              ></i>
                            </div>
                          </div>
                          <span class="rating-value">{{
                            service.rate_avg || $t("labels.notRated")
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// import VueApexCharts from "vue-apexcharts";

import ChartsAside from "@/components/Layouts/ChartsAside.vue";
import Loader from "@/components/Loaders/loader.vue";

import AreaCharts from "@/components/Charts/AreaCharts.vue";
import ColumnChart from "@/components/Charts/ColumnChart.vue";
import DonutChart from "@/components/Charts/DonutChart.vue";

export default {
  name: "HomePage",

  components: {
    ChartsAside,
    AreaCharts,
    ColumnChart,
    DonutChart,
    Loader,
  },

  data() {
    return {
      colors: [
        {
          first: "#62EAE1",
          secound: "rgba(126, 229, 223, 0)",
        },
        {
          first: "#FF6370",
          secound: "rgba(255, 99, 112, 0)",
        },
        {
          first: "#9E62EA",
          secound: " #B199ED",
        },
        {
          first: "#EABC62",
          secound: "#E9BA62",
        },
      ],

      // ========== Charts Aside
      asideIsActive: false,
      // start of main loader
      noData: true,
      loading: false,
      // end of main loader

      genderColors: ["#FF6B9D", "#4ECDC4"],

      // Chart data will be populated from your API
      statistics: {
        orders_count_graph: {
          dates: [],
          order_count: [],
        },
        orders_revenue_graph: {
          dates: [],
          total_price: [],
        },
        user_count_geneder_graph: {
          gender: [],
          users_count: [],
        },
        max_client_orderd: {},
        provider_services_count_per_group_service_table: [],
        provider_services_rate_avg_table: [],
        top_providers: [],
        top_rate_providers: [],
      },

      // ============ Charts Data
      cards: [],
      notifications_states: [],
      charts: [],
      top_cards: [],
      recently_partner: [],
      recently_rider: [],
      top_vehicle_package: [],
      top_providers: [],
      top_rider: [],
      chartsData: null,

      top_providers_headers: [
        {
          text: "الصورة",
          align: "center",
          value: "avatar",
          sortable: false,
        },
        {
          text: this.$t("labels.name"),
          value: "first_name",
          align: "center",
          sortable: false,
        },

        {
          text: this.$t("labels.email"),
          value: "email",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("labels.phone"),
          align: "center",
          value: "phone_complete_form",
          sortable: false,
        },
        {
          text: this.$t("labels.activation"),
          align: "center",
          value: "is_active",
          sortable: false,
        },

        {
          text: this.$t("labels.control"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],

      top_rate_providers_headers: [
        {
          text: "الصورة",
          align: "center",
          value: "avatar",
          sortable: false,
        },
        {
          text: this.$t("labels.fullname"),
          value: "first_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("labels.email"),
          value: "email",
          align: "center",
          sortable: false,
        },

        {
          text: this.$t("labels.phone"),
          align: "center",
          value: "phone_complete_form",
          sortable: false,
        },
        {
          text: this.$t("labels.is_active"),
          align: "center",
          value: "is_active",
          sortable: false,
        },

        {
          text: this.$t("labels.control"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.getters["auth_module/currentUser"];
    },

    ordersChartOptions() {
      return {
        chart: {
          type: "area",
          height: 350,
          toolbar: { show: false },
          background: "transparent",
        },
        colors: ["#667eea"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.7,
            opacityTo: 0.1,
            colorStops: [
              { offset: 0, color: "#667eea", opacity: 0.8 },
              { offset: 100, color: "#764ba2", opacity: 0.1 },
            ],
          },
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        grid: {
          borderColor: "#e7e7e7",
          strokeDashArray: 3,
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.statistics.orders_count_graph.dates,
          labels: {
            style: { colors: "#8e8da4" },
          },
        },
        yaxis: {
          labels: {
            style: { colors: "#8e8da4" },
          },
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function (val) {
              return val + " طلب";
            },
          },
        },
      };
    },

    ordersChartSeries() {
      return [
        {
          name: "عدد الطلبات",
          data: this.statistics.orders_count_graph.order_count,
        },
      ];
    },

    revenueChartOptions() {
      return {
        chart: {
          type: "bar",
          height: 350,
          toolbar: { show: false },
          background: "transparent",
        },
        colors: ["#f093fb"],
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.8,
            opacityTo: 0.4,
            colorStops: [
              { offset: 0, color: "#f093fb", opacity: 1 },
              { offset: 100, color: "#f5576c", opacity: 0.6 },
            ],
          },
        },
        plotOptions: {
          bar: {
            borderRadius: 8,
            columnWidth: "60%",
          },
        },
        grid: {
          borderColor: "#e7e7e7",
          strokeDashArray: 3,
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.statistics.orders_revenue_graph.dates,
          labels: {
            style: { colors: "#8e8da4", fontSize: "14px" },
          },
        },
        yaxis: {
          labels: {
            style: { colors: "#8e8da4", fontSize: "14px" },
            formatter: function (val) {
              return val + " ر.س";
            },
          },
        },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function (val) {
              return val + " ريال سعودي";
            },
          },
        },
      };
    },

    revenueChartSeries() {
      return [
        {
          name: "الإيرادات",
          data: this.statistics.orders_revenue_graph.total_price,
        },
      ];
    },

    genderChartOptions() {
      return {
        chart: {
          type: "donut",
          height: 250,
        },
        colors: this.genderColors,
        labels: this.statistics.user_count_geneder_graph.gender.map((g) =>
          g === "female" ? "إناث" : "ذكور"
        ),
        plotOptions: {
          pie: {
            donut: {
              size: "70%",
              labels: {
                show: true,
                total: {
                  show: true,
                  label: "إجمالي العملاء",
                  fontSize: "14px",
                  color: "#8e8da4",
                  formatter: () => {
                    return this.statistics.user_count_geneder_graph.users_count.reduce(
                      (a, b) => a + b,
                      0
                    );
                  },
                },
              },
            },
          },
        },
        legend: { show: false },
        tooltip: {
          theme: "dark",
          y: {
            formatter: function (val) {
              return val + " عميل";
            },
          },
        },
      };
    },

    genderChartSeries() {
      return this.statistics.user_count_geneder_graph.users_count;
    },
  },

  methods: {
    toggleAside() {
      this.asideIsActive = !this.asideIsActive;
    },
    getRouteLink(item) {
      let link;
      if (item.notify_type == "new_trip") {
        link = "trips";
      } else if (item.notify_type == "new_rider") {
        link = "riders";
      } else if (item.notify_type == "new_partner") {
        link = "partners";
      } else if (item.notify_type == "finish_trip") {
        link = "trips";
      }

      return link;
    },
    getHome() {
      this.canShow = this.$permission.allowed("home/show");
      if (!this.canShow) {
        this.loading = true;
        this.noData = false;
        this.axios({
          method: "GET",
          url: "home",
        })
          .then((res) => {
            this.noData = false;
            this.statistics = res.data.data;

            this.loading = false;
          })
          .catch((err) => {
            this.noData = true;
            this.loading = false;
            this.$iziToast.error({
              displayMode: 2,
              title: this.$t("error"),
              message:
                err.response?.data.message ?? err.response?.data.messages,
            });
          });
      }
    },

    getRandomColor(colors) {
      let random = Math.floor(Math.random() * 4);

      return { colors: colors[random], id: random };
    },

    imageError(index, inp) {
      this[inp][
        index
      ].avatar = require("@/assets/media/images/Icons/no-data.svg");
    },

    getPermissions() {
      this.axios.get("get_my_permissions").then((res) => {
        localStorage.setItem(
          "saloon_dash_permissions",
          JSON.stringify(res.data.data)
        );
        this.getHome();
        this.getSideBar();
      });
    },

    // getControls() {
    //   this.axios
    //     .get("get_my_control_permissions")
    //     .then((res) =>
    //       localStorage.setItem(
    //         "saloon_dash_provider_controls",
    //         JSON.stringify(res.data.data)
    //       )
    //     );
    // },

    getSideBar() {
      this.axios.get("admin_side_bar").then((res) => {
        this.$router.push(`/${res.data[0].categories[0].permissions[0].url}`);
      });
    },
  },

  mounted() {},

  created() {
    if (this.user.usertype != "superadmin") {
      this.getPermissions();
      this.getControls();
    } else {
      if (localStorage.getItem("saloon_dash_permissions")) {
        localStorage.removeItem("saloon_dash_permissions");
      }
      this.getHome();
    }
  },
};
</script>

<style scoped lang="scss">
.homePage {
  background: var(--mainBg);
  font-family: "Tajawal", sans-serif;
  color: var(--mainText);

  .fadeIn {
    animation: fadeIn 0.5s ease-out both;
  }

  // ========= Shared Card Style =========
  .chart-card,
  .analytics-card,
  .client-card,
  .service-card,
  .ratings-card {
    background: var(--cardBgColor);
    border-radius: 1rem;
    box-shadow: 0 8px 16px var(--simpleShadow);
    padding: 1.5rem;
    transition: transform 0.3s ease;
    backdrop-filter: blur(12px);

    &:hover {
      transform: translateY(-5px);
    }
  }

  // ========= Section Header =========
  .chart-header,
  .analytics-header,
  .client-header,
  .service-header,
  .ratings-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;

    .chart-icon,
    .analytics-icon,
    .client-badge,
    .service-icon,
    .ratings-icon {
      width: 42px;
      height: 42px;
      background: linear-gradient(135deg, var(--main), var(--mainColor-2));
      color: var(--lightText);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1rem;
      box-shadow: 0 0 6px var(--midShadow);
    }

    h3 {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--mainText);
      margin: 0;
    }

    p {
      font-size: 0.75rem;
      color: var(--mainColor-5);
      margin: 0;
    }
  }

  // ========= Chart Styling (Smaller Texts) =========
  ::v-deep .apexcharts-legend-text,
  ::v-deep .apexcharts-xaxis-label,
  ::v-deep .apexcharts-yaxis-label {
    font-size: 14px !important;
    color: var(--mainText) !important;
  }

  ::v-deep .apexcharts-title-text {
    font-size: 12px !important;
    font-weight: 500 !important;
  }

  ::v-deep .apexcharts-tooltip-text,
  ::v-deep .apexcharts-tooltip-title {
    font-size: 12px !important;
  }
  .chart-container.gender {
    font-size: 14px !important;
  }
  // ========= Client Card Special Styling =========
  .client-avatar-wrapper {
    position: relative;
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;

    .client-avatar {
      position: relative;
      width: 80px;
      height: 80px;
      border-radius: 50%;
      overflow: hidden;
      box-shadow: 0 0 10px var(--midShadow);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .client-status {
        position: absolute;
        bottom: 4px;
        right: 4px;
        width: 12px;
        height: 12px;
        background-color: var(--accent, #4ade80);
        border: 2px solid white;
        border-radius: 50%;
      }
    }

    .client-glow {
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        var(--mainColor-2_2) 0%,
        transparent 70%
      );
      animation: pulse 2s infinite ease-in-out;
    }
  }

  .client-info {
    text-align: center;

    .client-name {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--mainText);
    }

    .client-email {
      font-size: 0.8rem;
      color: var(--mainColor-5);
    }

    .client-stats {
      margin-top: 0.5rem;
      font-size: 0.75rem;
      color: var(--main);
    }
  }

  .ratings-grid {
    display: grid;
    gap: 15px;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  // ========= Gender Donut Legend =========
  .gender-legend {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 0.25rem;

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 50%;
      }

      .legend-label {
        font-size: 0.75rem;
      }

      .legend-count {
        font-weight: 600;
        color: var(--main);
      }
    }
  }

  // ========= Service Progress =========
  .service-card {
  background-color: var(--cardBgColor);
  border-radius: 1rem;
  padding: 1.5rem;
  box-shadow: 0 8px 16px var(--simpleShadow);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  .service-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1.2rem;

    .service-icon {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--main), var(--mainColor-2));
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 1.2rem;
      box-shadow: 0 0 6px var(--midShadow);
    }

    .service-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: var(--mainText);
      margin: 0;
    }
  }

  .service-list {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));


    .service-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      background-color: var(--mainBgColor);
      padding: 0.75rem 1rem;
      border-radius: 0.75rem;
      box-shadow: 0 2px 4px var(--simpleShadow);
      transition: all 0.3s ease;

      &:hover {
        background-color: var(--lighterGray);
        box-shadow: 0 4px 10px var(--midShadow);
      }

      .service-item-icon {
        width: 38px;
        height: 38px;
        background-color: var(--mainColor-3);
        color: var(--main);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        flex-shrink: 0;
      }

      .service-info {
        flex-grow: 1;

        .service-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: var(--mainText);
        }

        .service-orders {
          font-size: 0.8rem;
          color: var(--mainColor-5);
        }
      }

      .service-progress {
        flex-basis: 30%;
        height: 6px;
        background-color: var(--mainColor-4);
        border-radius: 4px;
        overflow: hidden;

        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, var(--main), var(--mainColor-2_2));
          transition: width 0.4s ease;
          border-radius: 4px;
        }
      }
    }
  }
}

  .service-progress {
    width: 100%;
    background-color: var(--mainColor-4);
    border-radius: 6px;
    overflow: hidden;
    height: 6px;
    margin-top: 0.5rem;

    .progress-bar {
      height: 100%;
      background: linear-gradient(90deg, var(--main), var(--mainColor-2_2));
      transition: width 0.4s ease;
    }
  }

  // ========= Rating Stars =========
  .stars-container {
  position: relative;
  display: inline-block;
  font-size: 1rem;
  line-height: 1;
  width: fit-content;

  .stars-bg,
  .stars-fill {
    display: flex;
    gap: 2px;
  }

  .stars-bg {
    color: #e0e0e0; // Gray background stars
  }

  .stars-fill {
    color: #facc15; // Gold filled stars
    position: absolute;
    top: 0;
    left: 0;
    white-space: nowrap;
    overflow: hidden;
    pointer-events: none;
  }
}
.rating-item {
  border-bottom: 1px solid var(--main);
  padding-bottom: 1rem;
}

  .rating-value {
    font-size: 0.75rem;
    margin-inline-start: 0.4rem;
    color: var(--mainText);
  }

  // ========= Table Section =========
  .table-title.title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--mainText);
    margin-bottom: 1rem;
  }

  .status {
    font-size: 0.7rem;
    padding: 4px 10px;
    border-radius: 50px;
    color: white;

    &.active {
      background-color: var(--accent, #48bb78);
    }

    &.inactive {
      background-color: var(--danger, #e53e3e);
    }
  }

  ._actions {
    display: flex;
    gap: 0.4rem;

    a {
      background-color: var(--main);
      color: white;
      font-size: 0.75rem;
      padding: 6px;
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: var(--mainColor-2);
      }
    }
  }

  // ========= Animation =========
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0% {
      transform: scale(0.95);
      opacity: 0.7;
    }
    70% {
      transform: scale(1);
      opacity: 0.2;
    }
    100% {
      transform: scale(0.95);
      opacity: 0.7;
    }
  }
}

.notifications_states {
  header {
    border-bottom: 1px solid #efefef !important;

    .title {
      font-style: normal;
      font-weight: 400;
      font-size: 16px !important;
      line-height: 20px;
      color: #000;
    }
  }

  .content {
    min-height: 200px;
    max-height: 250px;
    overflow-y: auto;
    padding-block: 8px;

    .notification_card {
      .state {
        display: flex;
        align-items: center;

        .notification_content {
          margin-inline-start: 8px;

          .title {
            display: block;
            font-style: normal;
            font-weight: 400;
            font-size: 12px !important;
            line-height: 17px;
            color: #000;
            margin: 0;
            margin-bottom: 5px;
          }

          p,
          a {
            font-style: normal;
            font-weight: 400 !important;
            font-size: 10px !important;
            line-height: 15px;
            color: var(--mainText);
            margin: 0;
          }
        }

        &:not(:last-child) {
          margin-bottom: 8px;
        }
      }

      .no-data {
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;

        h4 {
          font-size: 18px !important;
        }
      }
    }
  }

  footer {
    text-align: center;
    border-top: 1px solid #efefef !important;

    a {
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 17px;
      color: var(--mainText);
    }
  }

  &.client {
    .content {
      min-height: 250px;
      max-height: 250px;
    }
  }
}
</style>